import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle
} from 'react'
import {
  Input,
  Icon,
  Tooltip,
  message,
  Spin,
  Select,
  Button,
  Popover,
  Alert,
  Card,
  Table,
  Avatar,
  Tabs,
  Collapse,
  Modal,
  Badge
} from 'antd'
import classNames from 'classnames'
import {
  SortableContainer,
  SortableElement,
  sortableHandle,
  arrayMove as arrayMoveImmutable
} from 'react-sortable-hoc'

import StepTitle from '../StepTitle'
import StatusView from '../StatusView/StatusView'

import './ShotList.scss'
import {
  generateShots,
  mapCharacterToShots,
  getAvailableCharacters,
  groupShotsToScenes,
  getTaskStatus
} from '../../../../services/api/aiVideoGeneration'
import AddCharacterModal from './components/CharacterMappingTable/AddCharacterModal'
import CharacterMappingTable from './components/CharacterMappingTable'
import LocalReferencesView from './components/LocalReferences'
import PropReferencesView from './components/PropReferences'
import LocalShotJsonPatchModal from './components/LocalShotJsonPatchModal'

const { Option } = Select
const { Panel } = Collapse
const { TabPane } = Tabs

const hasFinalizedGuidelineReport = report => {
  if (!report) return false
  if (typeof report === 'string') return Boolean(report.trim())
  if (Array.isArray(report)) return report.length > 0
  if (typeof report !== 'object') return Boolean(report)

  const ignoredKeys = ['status', 'current_phase', 'currentPhase', 'phase']
  return Object.keys(report).some(key => !ignoredKeys.includes(key))
}

const normalizeVideoCreationMode = formData =>
  String(
    formData?.aiVideoFlowMode ||
      formData?.video_creation_mode ||
      formData?.videoCreationMode ||
      formData?.creativeDirection?.flowMode ||
      formData?.creativeDirection?.flow_mode ||
      ''
  ).toLowerCase()

const parseMaybeJSON = value => {
  if (value === undefined || value === null || value === '') return null
  if (typeof value !== 'string') return value

  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

const normalizeSelectedCharacters = value => {
  const parsed = parseMaybeJSON(value)
  if (!Array.isArray(parsed)) return []

  return parsed
    .map(item => {
      if (!item || typeof item !== 'object') return null
      const original = item.original ? String(item.original) : ''
      if (!original) return null

      const current = item.current || item.name || item.character || original

      return {
        ...item,
        original,
        last: item.last ? String(item.last) : original,
        current: String(current),
        tag: item.tag || item.character_id || item.id || null
      }
    })
    .filter(Boolean)
}

const characterSelectionFingerprint = value =>
  JSON.stringify(
    normalizeSelectedCharacters(value)
      .map(({ original, current, tag }) => ({
        original,
        current,
        tag: tag || null
      }))
      .sort((a, b) => a.original.localeCompare(b.original))
  )

const areCharacterSelectionsEqual = (a, b) =>
  characterSelectionFingerprint(a) === characterSelectionFingerprint(b)

const hasFinalizedCreativeDirection = formData => {
  const creativeDirection = formData?.creativeDirection || {}
  const reportCandidates = [
    creativeDirection.bible,
    creativeDirection.creative_bible,
    creativeDirection.creativeBible,
    creativeDirection.finalized_report,
    creativeDirection.finalizedReport,
    creativeDirection.creative_guideline,
    creativeDirection.creativeGuideline
  ]
  const report = reportCandidates.find(hasFinalizedGuidelineReport) || null
  const isFinalized =
    creativeDirection.finalized === true ||
    creativeDirection.isFinalized === true ||
    creativeDirection.status === 'finalized'
  const sessionId =
    formData?.sessionId ||
    formData?.session_id ||
    formData?.creative_guideline_session_id ||
    formData?.creative_guidelines_session_id ||
    formData?.creativeGuidelineSessionId ||
    formData?.creativeGuidelinesSessionId ||
    creativeDirection.sessionId ||
    creativeDirection.session_id ||
    creativeDirection.creative_guideline_session_id ||
    creativeDirection.creative_guidelines_session_id ||
    creativeDirection.creativeGuidelineSessionId ||
    creativeDirection.creativeGuidelinesSessionId ||
    null

  return Boolean(
    sessionId && isFinalized && hasFinalizedGuidelineReport(report)
  )
}

// Drag Handle Component
const DragHandle = sortableHandle(() => (
  <Tooltip title="Drag to reorder">
    <div className="drag-handle">
      <Icon type="drag" style={{ fontSize: '18px' }} />
    </div>
  </Tooltip>
))

// Character Highlighter Component (read-only, just highlights)
const CharacterHighlighter = ({ text, selectedCharacters = [] }) => {
  const displayText = getEditableText(text)

  const highlightCharacters = text => {
    if (!text || typeof text !== 'string') return text

    let processedText = text
    const highlights = []

    const placeholderRegex = /(_{2}|\*{2})([^_*]+)(_{2}|\*{2})/g
    let match
    while ((match = placeholderRegex.exec(text)) !== null) {
      highlights.push({
        start: match.index,
        end: match.index + match[0].length,
        text: match[0],
        isPlaceholder: true
      })
    }

    selectedCharacters.forEach(char => {
      if (char.current) {
        const currentName = char.current.replace(/^__|__$/g, '')
        const regex = new RegExp(`\\b${currentName}\\b`, 'g')
        let nameMatch
        while ((nameMatch = regex.exec(text)) !== null) {
          const isOverlapping = highlights.some(
            h => nameMatch.index >= h.start && nameMatch.index < h.end
          )
          if (!isOverlapping) {
            highlights.push({
              start: nameMatch.index,
              end: nameMatch.index + currentName.length,
              text: currentName,
              isPlaceholder: false
            })
          }
        }
      }
    })

    highlights.sort((a, b) => a.start - b.start)

    if (highlights.length === 0) return text

    const parts = []
    let lastIndex = 0

    highlights.forEach((highlight, idx) => {
      if (highlight.start > lastIndex) {
        parts.push(text.substring(lastIndex, highlight.start))
      }

      parts.push(
        <span key={`highlight-${idx}`} className="character-placeholder">
          {highlight.text}
        </span>
      )

      lastIndex = highlight.end
    })

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex))
    }

    return parts
  }

  return (
    <div className="character-highlighter">
      <div className="shot-direction-text">
        {highlightCharacters(displayText)}
      </div>
    </div>
  )
}

// Read-only display for other fields
const ReadOnlyField = ({ text }) => {
  return <div className="readonly-field">{getEditableText(text)}</div>
}

const FieldLabel = ({ label }) => (
  <div className="field-label-block">
    <label>{label}</label>
  </div>
)

const handleToggleKeyDown = (event, action) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    action()
  }
}

function getEditableText(value) {
  if (value == null) return ''

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value)
  }

  if (typeof value === 'object') {
    const textValue = value.text
    if (textValue == null) return ''
    return String(textValue)
  }

  return ''
}

function mergeEditableText(source, text) {
  if (source && typeof source === 'object' && !Array.isArray(source)) {
    return {
      ...source,
      text
    }
  }

  return text
}

const isReferenceResolved = reference =>
  Boolean(
    reference &&
      (reference.uri || reference.url || reference.image || reference.src)
  )

const isLocalhostRuntime = () => {
  if (typeof window === 'undefined') return false

  return ['localhost', '127.0.0.1', '::1', '0.0.0.0'].includes(
    window.location.hostname
  )
}

// Editable Field Component
const EditableField = ({
  text,
  selectedCharacters,
  onChange,
  emptyText = 'Click to add details.',
  ariaLabel,
  showEditIcon = false
}) => {
  const displayText = getEditableText(text)
  const [isEditing, setIsEditing] = useState(false)
  const [value, setValue] = useState(displayText)

  useEffect(() => {
    setValue(getEditableText(text))
  }, [text])

  if (isEditing) {
    return (
      <div className="editable-field-container">
        <Input.TextArea
          value={value}
          onChange={e => setValue(e.target.value)}
          autoSize={{ minRows: 2, maxRows: 6 }}
          aria-label={ariaLabel || 'Edit field'}
        />
        <div className="editable-field-actions">
          <Button
            size="small"
            onClick={() => {
              setIsEditing(false)
              setValue(getEditableText(text))
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            type="primary"
            onClick={() => {
              setIsEditing(false)
              onChange && onChange(value)
            }}
          >
            Save
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div
      className={classNames('editable-field-display', {
        'is-empty': !displayText
      })}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel || 'Edit field'}
      onClick={() => setIsEditing(true)}
      onKeyDown={event => handleToggleKeyDown(event, () => setIsEditing(true))}
    >
      <CharacterHighlighter
        text={displayText || emptyText}
        selectedCharacters={selectedCharacters}
      />
      {showEditIcon && <Icon type="edit" className="editable-field-icon" />}
    </div>
  )
}

// Shot Component (not sortable)
const Shot = ({
  shot,
  shotIndex,
  expandedPanelId,
  setExpandedPanelId,
  selectedCharacters,
  onShotChange
}) => {
  const { id, title, initial_image_prompt, video_prompt, dialogue } = shot
  const isExpanded = expandedPanelId === id
  const toggleShot = () => setExpandedPanelId(isExpanded ? null : id)

  return (
    <div className={classNames('shot-list-shot', { expanded: isExpanded })}>
      <div
        className="shot-header"
        role="button"
        tabIndex={0}
        onClick={toggleShot}
        onKeyDown={event => handleToggleKeyDown(event, toggleShot)}
      >
        <div className="shot-title-container">
          <div className="shot-title-main">
            <span className="shot-number">Shot {shotIndex + 1}</span>
            {title && <span className="shot-title-text">{title}</span>}
          </div>
        </div>
        <Button
          size="small"
          icon="edit"
          className="shot-edit-button"
          onClick={event => {
            event.stopPropagation()
            setExpandedPanelId(id)
          }}
        >
          Edit Shot
        </Button>
        <Icon
          type="right"
          rotate={isExpanded ? 90 : 0}
          className="expand-icon"
        />
      </div>

      <div
        className={classNames('shot-description-container', {
          expanded: isExpanded
        })}
      >
        <div className="shot-detail-field">
          <FieldLabel label="Title" />
          <ReadOnlyField text={title} />
        </div>

        <div className="shot-detail-field">
          <FieldLabel label="Initial Image Prompt" />
          <div className="editable-prompt-card">
            <EditableField
              text={initial_image_prompt}
              selectedCharacters={selectedCharacters}
              showEditIcon
              ariaLabel={`Edit initial image prompt for shot ${shotIndex + 1}`}
              onChange={val =>
                onShotChange?.(
                  'initial_image_prompt',
                  mergeEditableText(initial_image_prompt, val)
                )
              }
            />
          </div>
        </div>

        <div className="shot-detail-field">
          <FieldLabel label="Video Prompt" />
          <div className="editable-prompt-card">
            <EditableField
              text={video_prompt}
              selectedCharacters={selectedCharacters}
              showEditIcon
              ariaLabel={`Edit video prompt for shot ${shotIndex + 1}`}
              onChange={val =>
                onShotChange?.(
                  'video_prompt',
                  mergeEditableText(video_prompt, val)
                )
              }
            />
          </div>
        </div>

        {Object.keys(dialogue || {}).length > 0 && (
          <div className="shot-detail-field">
            <FieldLabel label="Dialogue" />
            <div className="editable-prompt-card">
              {Object.entries(dialogue || {}).map(([character, line], idx) => (
                <div key={idx} className="dialogue-entry">
                  <div className="dialogue-character">
                    <Icon type="message" />
                    <CharacterHighlighter
                      text={character}
                      selectedCharacters={selectedCharacters}
                    />
                  </div>
                  <div className="dialogue-line">
                    <EditableField
                      text={line}
                      selectedCharacters={selectedCharacters}
                      showEditIcon
                      ariaLabel={`Edit dialogue for ${character}`}
                      onChange={val => {
                        const newDialogue = { ...dialogue }
                        newDialogue[character] = mergeEditableText(line, val)
                        onShotChange && onShotChange('dialogue', newDialogue)
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Sortable Scene Component
const SortableScene = SortableElement(
  ({
    scene,
    sceneIndex,
    expandedPanelId,
    setExpandedPanelId,
    selectedCharacters,
    onSceneChange,
    audiomode
  }) => {
    const [isExpanded, setIsExpanded] = useState(false)
    const shotCount = scene.shots?.length || 0
    const toggleScene = () => setIsExpanded(!isExpanded)

    return (
      <Card
        className="scene-card"
        title={
          <div
            className="scene-header"
            role="button"
            tabIndex={0}
            onClick={toggleScene}
            onKeyDown={event => handleToggleKeyDown(event, toggleScene)}
          >
            <DragHandle />
            <div className="scene-header-copy">
              <div className="scene-header-topline">
                <span className="scene-number">Scene {sceneIndex + 1}</span>
                <span className="scene-shot-count">
                  {shotCount} {shotCount === 1 ? 'shot' : 'shots'}
                </span>
              </div>
              <div className="scene-title-row">
                <span className="scene-edit-hint">
                  Location, narration, and shot prompts
                </span>
              </div>
            </div>
            <Button
              size="small"
              icon="edit"
              className="scene-edit-button"
              onClick={event => {
                event.stopPropagation()
                setIsExpanded(true)
              }}
            >
              Edit Scene
            </Button>
            <Icon
              type="right"
              rotate={isExpanded ? 90 : 0}
              className="scene-expand-icon"
            />
          </div>
        }
      >
        <div className="scene-info">
          <div className="scene-detail-field">
            <FieldLabel label="Location" />
            <div className="editable-prompt-card scene-location-card">
              <EditableField
                text={scene.description}
                selectedCharacters={selectedCharacters}
                emptyText="Add the scene location."
                ariaLabel={`Edit location for scene ${sceneIndex + 1}`}
                onChange={val => onSceneChange?.('description', val)}
              />
            </div>
          </div>

          {scene.aggregated_voiceover && (
            <div className="scene-detail-field">
              <FieldLabel label="Narration Voiceover" />
              <div className="editable-prompt-card">
                <EditableField
                  text={scene.aggregated_voiceover}
                  selectedCharacters={selectedCharacters}
                  ariaLabel={`Edit narration for scene ${sceneIndex + 1}`}
                  onChange={val =>
                    onSceneChange?.(
                      'aggregated_voiceover',
                      mergeEditableText(scene.aggregated_voiceover, val)
                    )
                  }
                />
              </div>
            </div>
          )}

          {isExpanded && (
            <div className="scene-shots">
              <div className="scene-shots-heading">
                <div>
                  <h4>Shots ({scene.shots.length})</h4>
                  <p>
                    Review shot titles and refine prompts before generation.
                  </p>
                </div>
              </div>
              {scene.shots.map((shot, index) => (
                <Shot
                  key={`${scene.id}-shot-${index}`}
                  shot={shot}
                  shotIndex={index}
                  expandedPanelId={expandedPanelId}
                  setExpandedPanelId={setExpandedPanelId}
                  selectedCharacters={selectedCharacters}
                  onShotChange={(field, value) => {
                    const updatedShots = [...scene.shots]
                    updatedShots[index] = {
                      ...updatedShots[index],
                      [field]: value
                    }
                    onSceneChange && onSceneChange('shots', updatedShots)
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </Card>
    )
  }
)

// Sortable Scene List Component
const SortableSceneList = SortableContainer(
  ({
    scenes,
    expandedPanelId,
    setExpandedPanelId,
    selectedCharacters,
    isExpanded,
    onToggleExpand,
    onSceneListChange,
    audiomode
  }) => {
    return (
      <Card
        className="expandable-card"
        title={
          <div className="expandable-card-header" onClick={onToggleExpand}>
            <Icon
              type="right"
              className={classNames('expand-icon', {
                'is-expanded': isExpanded
              })}
            />
            <span className="header-title">Scenes</span>
            <span className="header-count">{scenes.length}</span>
          </div>
        }
        bodyStyle={{
          padding: isExpanded ? '24px' : '0',
          display: isExpanded ? 'block' : 'none',
          overflow: 'visible'
        }}
        style={{ marginBottom: '24px' }}
      >
        <div className="scenes-edit-helper">
          <Icon type="edit" />
          <div>
            <strong>Review and refine scenes before generation.</strong>
            <p>
              Edit scene narration, locations, shot prompts, and timing when
              needed.
            </p>
          </div>
        </div>
        <div className="scene-list">
          {scenes.map((scene, index) => (
            <SortableScene
              key={`scene-${index}`}
              index={index}
              scene={scene}
              sceneIndex={index}
              expandedPanelId={expandedPanelId}
              setExpandedPanelId={setExpandedPanelId}
              selectedCharacters={selectedCharacters}
              audiomode={audiomode}
              onSceneChange={(field, value) => {
                const updatedScenes = [...scenes]
                updatedScenes[index] = {
                  ...updatedScenes[index],
                  [field]: value
                }
                onSceneListChange && onSceneListChange(updatedScenes)
              }}
            />
          ))}
        </div>
      </Card>
    )
  }
)

const ShotList = forwardRef((props, ref) => {
  const {
    formData,
    setFormData,
    onValidationChange,
    isGeneratingLocationReference,
    shotsGenerationProgress,
    onRegenerateLocationReference,
    onAddLocationReference,
    onEditLocationReference,
    onBatchUpdateLocations,
    isClusteringAudio,
    isFetchingCharacters,
    characters: propsCharacters,
    setCharacters: setPropsCharacters,
    fetchCharacters,
    locationGenerationStatus,
    locationGenerationProgress,
    isGeneratingPropReference,
    propGenerationProgress,
    propGenerationStatus,
    onRegeneratePropReference,
    onAddPropReference,
    onEditPropReference,
    onBatchUpdateProps,
    videoId,
    fetchingShots,
    isClustering,
    clusteringProgress
  } = props

  const [scenes, setScenes] = useState([])
  const [expandedPanelId, setExpandedPanelId] = useState(null)

  const [activeTab, setActiveTab] = useState('1')
  const [initialCharacters, setInitialCharacters] = useState([])
  const [changedCharacters, setChangedCharacters] = useState([])

  const [isScenesExpanded, setIsScenesExpanded] = useState(true)
  const [showAddCharacterModal, setShowAddCharacterModal] = useState(false)
  const [showLocalJsonPatchModal, setShowLocalJsonPatchModal] = useState(false)
  const showLocalShotTools = isLocalhostRuntime()

  const {
    characterList = [],
    shotList = [],
    shotLoading = false,
    shotGenerationError = '',
    shotsApiRes = {},
    selectedCharacter = []
  } = formData

  const characters = propsCharacters || []
  const isAdvancedMode = normalizeVideoCreationMode(formData) === 'advanced'
  const normalizedSelectedCharacter = useMemo(
    () => normalizeSelectedCharacters(selectedCharacter),
    [selectedCharacter]
  )

  useEffect(() => {
    if (shotList.length > 0) {
      setScenes(shotList)
    }
  }, [])

  useEffect(() => {
    if (normalizedSelectedCharacter.length === 0) return

    setChangedCharacters(prev =>
      areCharacterSelectionsEqual(prev, normalizedSelectedCharacter)
        ? prev
        : normalizedSelectedCharacter
    )
    setInitialCharacters(prev =>
      areCharacterSelectionsEqual(prev, normalizedSelectedCharacter)
        ? prev
        : normalizedSelectedCharacter
    )
  }, [normalizedSelectedCharacter])

  useEffect(() => {
    if (shotList.length > 0) {
      setScenes(shotList)
    }
  }, [shotList])

  useEffect(() => {
    if (characters.length > 0) {
      setFormData(prev => ({ ...prev, characterList: characters }))
    }
  }, [characters, setFormData])

  useEffect(() => {
    if (changedCharacters.length === 0) return

    setFormData(prev => {
      if (
        areCharacterSelectionsEqual(prev.selectedCharacter, changedCharacters)
      ) {
        return prev
      }

      return { ...prev, selectedCharacter: changedCharacters }
    })
  }, [changedCharacters, setFormData])

  // Enhanced function to extract characters from both original and current text
  function extractCharacters(scenesArray) {
    const characterSet = new Set()

    // Regex to match both **character1** and __character1__ formats
    const characterRegex = /(?:\*\*character\d+\*\*|__character\d+__)/g

    function searchInValue(value) {
      if (typeof value === 'string') {
        const matches = value.match(characterRegex)
        if (matches) {
          matches.forEach(match => characterSet.add(match))
        }
      } else if (Array.isArray(value)) {
        // Handle arrays
        value.forEach(item => searchInValue(item))
      } else if (typeof value === 'object' && value !== null) {
        // Handle objects
        Object.values(value).forEach(val => searchInValue(val))
      }
    }

    // Search through the entire scenes array
    searchInValue(scenesArray)

    return Array.from(characterSet)
  }

  // Enhanced character change handler that works from CharacterMappingTable
  const handleCharacterChange = (
    oldCharacter,
    newCharacter,
    _firstCharacter,
    characterId
  ) => {
    console.log(`Replacing "${oldCharacter}" with "${newCharacter}"`)

    const oldCharWithUnderscores = `__${oldCharacter.replace(/^__|__$/g, '')}__`
    const newCharWithUnderscores = `__${newCharacter}__`

    // Update character mappings in selectedCharacter
    let updatedChars = [...changedCharacters]
    const existingCharIndex = updatedChars.findIndex(
      char =>
        char.original === oldCharWithUnderscores ||
        (char.original &&
          char.original.replace(/^__|__$/g, '') ===
            oldCharacter.replace(/^__|__$/g, ''))
    )

    if (existingCharIndex !== -1) {
      updatedChars[existingCharIndex] = {
        ...updatedChars[existingCharIndex],
        last: updatedChars[existingCharIndex].current,
        current: newCharWithUnderscores,
        tag: characterId
      }
    } else {
      updatedChars.push({
        original: oldCharWithUnderscores,
        last: oldCharWithUnderscores,
        current: newCharWithUnderscores,
        tag: characterId
      })
    }

    setChangedCharacters(updatedChars)

    message.success(
      `Changed "${oldCharacter}" to "${newCharacter}" throughout all scenes and shots`
    )
  }

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const updatedScenes = arrayMoveImmutable(scenes, oldIndex, newIndex)
    setScenes(updatedScenes)
    setFormData(prev => ({ ...prev, shotList: updatedScenes }))
  }

  // Helper to check if characterMap is valid and has entries
  const isCharacterMapValid = map => {
    if (!map) return false
    if (typeof map === 'string') {
      try {
        const parsed = JSON.parse(map)
        return (
          parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0
        )
      } catch {
        return false
      }
    }
    return typeof map === 'object' && Object.keys(map).length > 0
  }

  // Get total characters - prioritize characterMap keys if valid, otherwise extract from shots
  const totalCharacters = useMemo(() => {
    const charMap = formData.characterMap

    // Use characterMap keys if it's a valid non-empty object
    if (isCharacterMapValid(charMap)) {
      const mapToUse =
        typeof charMap === 'string' ? JSON.parse(charMap) : charMap
      return Object.keys(mapToUse)
    }

    // Fallback: extract characters from shots
    const originalChars = extractCharacters(shotList)
    return Array.from(new Set(originalChars))
  }, [formData.characterMap, shotList])

  const isAllCharactersChanged =
    totalCharacters.length === 0 ||
    totalCharacters.every(char => {
      const normalizedChar = char.replace(/^__|__$/g, '')
      return changedCharacters.some(c => {
        const normalizedC = String(c?.original || '').replace(/^__|__$/g, '')
        return normalizedC === normalizedChar
      })
    })

  const allLocationsGenerated = useMemo(() => {
    const locMap = formData.locationMap || {}
    const locRef = formData.locationReference || {}
    const keys = Object.keys(locMap)
    if (keys.length === 0) return true
    return keys.every(key => {
      const ref = locRef[key] || locRef[`__${key}__`]
      return isReferenceResolved(ref)
    })
  }, [formData.locationMap, formData.locationReference])

  const allPropsGenerated = useMemo(() => {
    const propMap = formData.propMap || {}
    const propRef = formData.props_reference || {}
    const keys = Object.keys(propMap)
    if (keys.length === 0) return true
    return keys.every(key => {
      const ref = propRef[key]
      return isReferenceResolved(ref)
    })
  }, [formData.propMap, formData.props_reference])

  const allOutfitsMapped = useMemo(() => {
    if (totalCharacters.length === 0) return true

    const wardrobe = formData.wardrobe || {}
    const outfitsRef = formData.outfits_reference || {}
    const keyVariants = key => {
      const normalized = String(key || '').replace(/^__|__$/g, '')
      return [key, normalized, `__${normalized}__`]
    }
    const getByKeyVariants = (obj, key, fallback = null) => {
      for (const variant of keyVariants(key)) {
        if (variant && Object.prototype.hasOwnProperty.call(obj, variant)) {
          return obj[variant]
        }
      }
      return fallback
    }

    return totalCharacters.every(char => {
      const rawOutfits = getByKeyVariants(wardrobe, char, [])
      const charOutfits = Array.isArray(rawOutfits)
        ? rawOutfits
        : rawOutfits
        ? [rawOutfits]
        : []
      if (charOutfits.length === 0) return true

      const charRefs = getByKeyVariants(outfitsRef, char, {})

      // Check if every outfit in wardrobe has a reference
      return charOutfits.every(outfit => {
        return !!getByKeyVariants(charRefs, outfit)
      })
    })
  }, [totalCharacters, formData.wardrobe, formData.outfits_reference])

  const unmappedCharactersCount = useMemo(() => {
    return totalCharacters.filter(char => {
      const normalizedChar = char.replace(/^__|__$/g, '')
      return !changedCharacters.some(c => {
        const normalizedC = String(c?.original || '').replace(/^__|__$/g, '')
        return normalizedC === normalizedChar
      })
    }).length
  }, [totalCharacters, changedCharacters])

  const ungeneratedLocationsCount = useMemo(() => {
    const locMap = formData.locationMap || {}
    const locRef = formData.locationReference || {}
    const keys = Object.keys(locMap)
    return keys.filter(key => {
      const ref = locRef[key] || locRef[`__${key}__`]
      return !isReferenceResolved(ref)
    }).length
  }, [formData.locationMap, formData.locationReference])

  const ungeneratedPropsCount = useMemo(() => {
    const propMap = formData.propMap || {}
    const propRef = formData.props_reference || {}
    const keys = Object.keys(propMap)
    return keys.filter(key => {
      const ref = propRef[key]
      return !isReferenceResolved(ref)
    }).length
  }, [formData.propMap, formData.props_reference])

  const ungeneratedOutfitsCount = useMemo(() => {
    if (totalCharacters.length === 0) return 0

    const wardrobe = formData.wardrobe || {}
    const outfitsRef = formData.outfits_reference || {}
    const keyVariants = key => {
      const normalized = String(key || '').replace(/^__|__$/g, '')
      return [key, normalized, `__${normalized}__`]
    }
    const getByKeyVariants = (obj, key, fallback = null) => {
      for (const variant of keyVariants(key)) {
        if (variant && Object.prototype.hasOwnProperty.call(obj, variant)) {
          return obj[variant]
        }
      }
      return fallback
    }

    return totalCharacters.reduce((acc, char) => {
      const rawOutfits = getByKeyVariants(wardrobe, char, [])
      const charOutfits = Array.isArray(rawOutfits)
        ? rawOutfits
        : rawOutfits
        ? [rawOutfits]
        : []
      const charRefs = getByKeyVariants(outfitsRef, char, {})
      if (!Array.isArray(charOutfits) || charOutfits.length === 0) return acc

      const missingForCharacter = charOutfits.filter(outfit => {
        return !getByKeyVariants(charRefs, outfit)
      }).length

      return acc + missingForCharacter
    }, 0)
  }, [totalCharacters, formData.wardrobe, formData.outfits_reference])

  const shotListRequirementsCount =
    unmappedCharactersCount + ungeneratedOutfitsCount

  const usesFinalizedCreativeDirection =
    isAdvancedMode && hasFinalizedCreativeDirection(formData)

  const isValid = useMemo(() => {
    const hasShots = scenes.length > 0
    return (
      hasShots &&
      isAllCharactersChanged &&
      (usesFinalizedCreativeDirection || allLocationsGenerated) &&
      (usesFinalizedCreativeDirection || allPropsGenerated) &&
      allOutfitsMapped
    )
  }, [
    scenes,
    isAllCharactersChanged,
    allLocationsGenerated,
    allPropsGenerated,
    allOutfitsMapped,
    usesFinalizedCreativeDirection
  ])

  console.log({
    isValid,
    isAllCharactersChanged,
    allLocationsGenerated,
    allPropsGenerated,
    allOutfitsMapped,
    totalCharacters
  })

  const hasChanges = useMemo(() => {
    if (changedCharacters.length !== initialCharacters.length) return true

    const normalize = arr =>
      arr
        .slice()
        .sort((a, b) => a.original.localeCompare(b.original))
        .map(({ original, current }) => ({ original, current }))

    const a = normalize(changedCharacters)
    const b = normalize(initialCharacters)

    return JSON.stringify(a) !== JSON.stringify(b)
  }, [changedCharacters, initialCharacters])

  useEffect(() => {
    onValidationChange(isValid)
  }, [isValid])

  const handleAddCharacter = () => {
    setShowAddCharacterModal(true)
  }

  const handleCharacterAddition = _character => {
    // Refresh characters from the parent hook
    if (fetchCharacters) {
      fetchCharacters(true) // force refresh
    }
  }

  // Handler to add a new character to the characterMap (legacy format for now)
  const handleAddNewCharacter = (characterId, characterName) => {
    setFormData(prev => ({
      ...prev,
      characterMap: {
        ...(prev.characterMap || {}),
        [characterId]: characterName
      }
    }))
  }

  // Handler to delete a character from the characterMap
  const handleDeleteCharacter = characterKey => {
    setFormData(prev => {
      const updatedMap = { ...(prev.characterMap || {}) }
      delete updatedMap[characterKey]
      return {
        ...prev,
        characterMap: updatedMap
      }
    })

    // Also remove from changedCharacters if present
    setChangedCharacters(prev =>
      prev.filter(char => char.original !== characterKey)
    )
  }

  const handleClearShotMedia = (shotIds, clearFinalVideo = false) => {
    if (!shotIds || shotIds.length === 0) return

    setFormData(prev => {
      const updatedShotList = prev.shotList.map(scene => ({
        ...scene,
        shots: scene.shots.map(shot => {
          if (shotIds.includes(shot.id)) {
            return {
              ...shot,
              imageSrc: null,
              vidSrc: null
            }
          }
          return shot
        })
      }))

      return {
        ...prev,
        shotList: updatedShotList,
        finalVideo: clearFinalVideo ? null : prev.finalVideo
      }
    })
  }

  const handleWardrobeSave = (characterKey, updatedTagsData) => {
    setFormData(prev => ({
      ...prev,
      outfits_reference: {
        ...(prev.outfits_reference || {}),
        [characterKey]: updatedTagsData
      }
    }))
  }

  const handleRegenerateShots = () => {
    Modal.confirm({
      title: 'Are you sure you want to regenerate shots?',
      content:
        'This will overwrite your existing shots and any edits you have made.',
      okText: 'Yes, Regenerate',
      cancelText: 'Cancel',
      onOk: () => {
        props.onRegenerateShots()
      },
      centered: true
    })
  }

  if (shotLoading || fetchingShots) {
    const loadingTitle = isFetchingCharacters
      ? 'Fetching characters...'
      : isClustering
      ? 'Clustering scenes...'
      : isGeneratingLocationReference
      ? 'Generating locations...'
      : 'Generating your shots...'

    const loadingProgress = isFetchingCharacters
      ? null
      : isClustering
      ? clusteringProgress
      : isGeneratingLocationReference
      ? locationGenerationProgress
      : shotsGenerationProgress

    return (
      <div className="shot-list-shots">
        <StatusView
          status="loading"
          title={loadingTitle}
          subTitle="Please wait! This may take a few minutes."
          progress={loadingProgress}
          inline
        />
      </div>
    )
  }

  return (
    <div className="shot-list-shots">
      <div className="shot-list-shots__header">
        <StepTitle
          iconType="ordered-list"
          title="Shot List"
          description="Map characters first, then drag to reorder scenes."
        />

        <div className="shot-list-shots__header-actions">
          {isGeneratingLocationReference && (
            <div
              className="shot-list-shots__location-status"
              role="status"
              aria-live="polite"
            >
              <Icon type="loading" />
              <span>Generating location images...</span>
            </div>
          )}
          {showLocalShotTools && (
            <Button
              onClick={() => setShowLocalJsonPatchModal(true)}
              icon="code"
              size="large"
              className="shot-list-shots__action-btn"
            >
              JSON Patch
            </Button>
          )}
          <Button
            type="primary"
            onClick={handleRegenerateShots}
            loading={shotLoading || fetchingShots}
            disabled={shotLoading || fetchingShots}
            icon="reload"
            size="large"
            className="shot-list-shots__regenerate-btn"
          >
            Regenerate Shots
          </Button>
        </div>
      </div>

      {shotGenerationError && shotList.length === 0 ? (
        <Alert
          type="error"
          showIcon
          message="Shot generation failed"
          description="The last shot generation request failed. Please fix the backend error or try regenerating again."
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        style={{ marginTop: 16 }}
      >
        <TabPane
          tab={
            <Tooltip
              title={
                shotListRequirementsCount > 0
                  ? `${unmappedCharactersCount} characters need mapping, ${ungeneratedOutfitsCount} outfits need references`
                  : 'All characters and outfits are mapped'
              }
            >
              <Badge
                count={shotListRequirementsCount}
                overflowCount={99}
                style={{
                  backgroundColor:
                    shotListRequirementsCount > 0 ? '#d4380d' : '#52c41a',
                  boxShadow: '0 0 0 1px #fff'
                }}
                offset={[12, 0]}
              >
                <span
                  style={{
                    color:
                      shotListRequirementsCount > 0 ? '#d4380d' : 'inherit',
                    fontWeight: shotListRequirementsCount > 0 ? 600 : 'normal',
                    paddingRight: shotListRequirementsCount > 0 ? 8 : 0
                  }}
                >
                  {shotListRequirementsCount > 0 && (
                    <Icon type="warning" style={{ marginRight: 8 }} />
                  )}
                  Shot List
                </span>
              </Badge>
            </Tooltip>
          }
          key="1"
        >
          {totalCharacters.length > 0 && (
            <CharacterMappingTable
              formData={formData}
              setFormData={setFormData}
              placeholderCharacters={totalCharacters}
              characters={characters}
              onCharacterChange={handleCharacterChange}
              changedCharacters={changedCharacters}
              onAddCharacter={handleAddCharacter}
              isFetchingCharacters={isFetchingCharacters}
              characterMap={formData.characterMap || {}}
              isAllCharactersChanged={isAllCharactersChanged}
              shotList={shotList}
              onAddNewCharacter={handleAddNewCharacter}
              onDeleteCharacter={handleDeleteCharacter}
              onClearShotMedia={handleClearShotMedia}
              fetchCharacters={fetchCharacters}
              wardrobe={formData.wardrobe}
              outfitMap={formData.outfitMap}
              outfitsReference={formData.outfits_reference}
              onWardrobeSave={handleWardrobeSave}
              videoId={videoId}
            />
          )}

          <SortableSceneList
            scenes={scenes}
            expandedPanelId={expandedPanelId}
            setExpandedPanelId={setExpandedPanelId}
            characters={characters}
            onCharacterChange={handleCharacterChange}
            onSortEnd={onSortEnd}
            useDragHandle
            axis="y"
            helperClass="dragging-scene"
            isExpanded={isScenesExpanded}
            onToggleExpand={() => setIsScenesExpanded(!isScenesExpanded)}
            selectedCharacters={changedCharacters}
            audiomode={formData.audiomode}
            onSceneListChange={updatedScenes => {
              setScenes(updatedScenes)
              setFormData(prev => ({ ...prev, shotList: updatedScenes }))
            }}
          />
        </TabPane>

        <TabPane
          tab={
            <Tooltip
              title={
                ungeneratedLocationsCount > 0
                  ? `${ungeneratedLocationsCount} locations need generation`
                  : 'All locations generated'
              }
            >
              <Badge
                count={ungeneratedLocationsCount}
                overflowCount={99}
                style={{
                  backgroundColor:
                    ungeneratedLocationsCount > 0 ? '#d4380d' : '#52c41a',
                  boxShadow: '0 0 0 1px #fff'
                }}
                offset={[12, 0]}
              >
                <span
                  style={{
                    color:
                      ungeneratedLocationsCount > 0 &&
                      !isGeneratingLocationReference
                        ? '#d4380d'
                        : 'inherit',
                    fontWeight:
                      ungeneratedLocationsCount > 0 &&
                      !isGeneratingLocationReference
                        ? 600
                        : 'normal',
                    paddingRight: ungeneratedLocationsCount > 0 ? 8 : 0
                  }}
                >
                  {isGeneratingLocationReference && (
                    <Icon type="loading" style={{ marginRight: 8 }} />
                  )}
                  {ungeneratedLocationsCount > 0 &&
                    !isGeneratingLocationReference && (
                      <Icon type="warning" style={{ marginRight: 8 }} />
                    )}
                  Locations
                </span>
              </Badge>
            </Tooltip>
          }
          key="2"
        >
          {(formData.locationMap || formData.locationReference) && (
            <LocalReferencesView
              formData={formData}
              locationMap={formData.locationMap || {}}
              locationReference={formData.locationReference || {}}
              scenes={scenes}
              setFormData={setFormData}
              onRegenerateLocationReference={onRegenerateLocationReference}
              onAddLocationReference={onAddLocationReference}
              onEditLocationReference={onEditLocationReference}
              isGeneratingLocationReference={isGeneratingLocationReference}
              onBatchUpdateLocations={onBatchUpdateLocations}
              locationGenerationProgress={locationGenerationProgress}
              locationGenerationStatus={locationGenerationStatus}
              videoId={videoId}
            />
          )}
        </TabPane>

        <TabPane
          tab={
            <Tooltip
              title={
                ungeneratedPropsCount > 0
                  ? `${ungeneratedPropsCount} props need generation`
                  : 'All props generated'
              }
            >
              <Badge
                count={ungeneratedPropsCount}
                overflowCount={99}
                style={{
                  backgroundColor:
                    ungeneratedPropsCount > 0 ? '#d4380d' : '#52c41a',
                  boxShadow: '0 0 0 1px #fff'
                }}
                offset={[12, 0]}
              >
                <span
                  style={{
                    color:
                      ungeneratedPropsCount > 0 && !isGeneratingPropReference
                        ? '#d4380d'
                        : 'inherit',
                    fontWeight:
                      ungeneratedPropsCount > 0 && !isGeneratingPropReference
                        ? 600
                        : 'normal',
                    paddingRight: ungeneratedPropsCount > 0 ? 8 : 0
                  }}
                >
                  {isGeneratingPropReference && (
                    <Icon type="loading" style={{ marginRight: 8 }} />
                  )}
                  {ungeneratedPropsCount > 0 && !isGeneratingPropReference && (
                    <Icon type="warning" style={{ marginRight: 8 }} />
                  )}
                  Props
                </span>
              </Badge>
            </Tooltip>
          }
          key="3"
        >
          {(formData.propMap || formData.props_reference) && (
            <PropReferencesView
              formData={formData}
              propMap={formData.propMap || {}}
              propReference={formData.props_reference || {}}
              scenes={scenes}
              setFormData={setFormData}
              onRegeneratePropReference={onRegeneratePropReference}
              onAddPropReference={onAddPropReference}
              onEditPropReference={onEditPropReference}
              isGeneratingPropReference={isGeneratingPropReference}
              onBatchUpdateProps={onBatchUpdateProps}
              propGenerationProgress={propGenerationProgress}
              propGenerationStatus={propGenerationStatus}
              videoId={videoId}
            />
          )}
        </TabPane>

        <TabPane tab="Story" key="4">
          <Card title="Generated Story" style={{ marginBottom: '20px' }}>
            {formData.voiceoverText ? (
              <div>
                <p>{formData.voiceoverText}</p>
              </div>
            ) : (
              <p style={{ color: '#999' }}>No story text available.</p>
            )}
          </Card>
        </TabPane>
      </Tabs>

      {showAddCharacterModal && (
        <AddCharacterModal
          onClose={() => setShowAddCharacterModal(false)}
          onCreate={handleCharacterAddition}
          videoId={videoId}
          formData={formData}
        />
      )}

      {showLocalShotTools && (
        <LocalShotJsonPatchModal
          visible={showLocalJsonPatchModal}
          scenes={scenes}
          onClose={() => setShowLocalJsonPatchModal(false)}
          onApply={updatedScenes => {
            setScenes(updatedScenes)
            setFormData(prev => ({ ...prev, shotList: updatedScenes }))
          }}
        />
      )}
    </div>
  )
})

export default ShotList
